import lt from './lt.json';

/** The full dictionary for each language — one flat, hand-edited file per
 *  language, sorted alphabetically by key. Console and Driver App strings
 *  share this file; driver-only keys use a `d.` prefix. */

/**
 * WRITING A LITHUANIAN STRING THAT CONTAINS A NUMBER
 * ==================================================
 * Lithuanian numeral agreement takes THREE forms, and a template holds one:
 *
 *     1 pildymas · 2 pildymai · 11 pildymų
 *
 * So `"{n} pildymų"` is correct for 11 and wrong for the two numbers a
 * carrier sees most often. V-33 found ten strings in this file written that
 * way — "1 pildymų", "1 atvejai", "2 eilutė" — every one of them true only
 * at values nobody hits.
 *
 * THE FIX IS THE GENITIVE DODGE: put the noun in the genitive plural BEFORE
 * the number, where it does not agree with anything, and let the number
 * follow.
 *
 *     BAD   "{n} pildymų nepriskirta"
 *     GOOD  "Nepriskirtų pildymų: {n}"
 *
 * core/i18n.py and core/receivables_reminder.py use the same device on the
 * server side ("užsakymų: {orders}", "Nepažymėtų kaip išsiųstų klientui:
 * {n}"), so both halves of the product read the same way.
 *
 * These constructions are already safe and need no dodge:
 *   - anything after `iš`  — "iš {n} reisų" is genitive at every number;
 *   - an abbreviation      — "{n} sustojim.", "{n} eil." have no plural;
 *   - a neuter predicate   — "{n} apribota", "{n} perrašyta";
 *   - a 3rd-person verb    — "{n} laukia" is the same for one and for forty.
 *
 * There is deliberately no automated check. Every formulation above is
 * legitimate, so a scanner would either miss half the mistakes or flag
 * forty correct strings — and a guard that cries wolf gets suppressed,
 * which is worse than this comment.
 */
/**
 * The catalogues this page has in memory. LT is here from the first byte;
 * anything else arrives through ensureLanguage() below.
 *
 * V-98. Measured 2026-08-27: the two catalogues together are 224 852 bytes
 * of JSON — 72,8 KiB gzip — and until this change BOTH shipped inside the
 * `queryClient` chunk that all three entry points load. That chunk was the
 * single largest file in the build, larger than React and react-dom
 * together, and 38% of everything a driver's phone downloaded before it
 * could show a button. Every Lithuanian carrier — which is all of them —
 * was paying 34,9 KiB gzip for the English console strings on every first
 * load of every app, on a phone in a cab.
 *
 * Mutable on purpose, and the only mutable state in this file. The
 * alternative is a second dictionary object passed through every call site,
 * which is a bigger change to a hotter path for the same result.
 */
export const LOCALES = { lt };

/**
 * Written out rather than derived from LOCALES, which is what it used to be.
 * The moment a catalogue can be absent, `Object.keys(LOCALES)` answers "this
 * build supports Lithuanian only" — and the language toggle would then
 * render one button, permanently, on a page that has simply not needed
 * English yet. What a build SUPPORTS and what it has LOADED are two
 * different questions from here on.
 */
export const SUPPORTED_LANGS = ['lt', 'en'];

/**
 * How a language that is not built in gets here. One dynamic import per
 * language, which is what makes Rollup emit it as its own chunk instead of
 * folding it into the shared one.
 */
const LOADERS = {
  en: () => import('./en.json'),
};

const pending = new Map();

/** True when translate() can already answer in this language. */
export function isLoaded(lang) {
  return Object.prototype.hasOwnProperty.call(LOCALES, lang);
}

/**
 * Load a language's catalogue, once.
 *
 * Callers await this BEFORE showing the language, never after: rendering
 * first and swapping the strings in when they arrive would show a Lithuanian
 * screen to an English reader for a beat, and the reader most likely to see
 * that is a client opening a delivery link on an English phone — someone who
 * has no idea this app has two languages and would simply read it as broken.
 *
 * Resolves to true when the language is usable, false when it could not be
 * fetched. False is not an error to show anybody: translate() falls back to
 * Lithuanian, which is what the whole app did before this split existed.
 */
export function ensureLanguage(lang) {
  if (isLoaded(lang)) return Promise.resolve(true);
  const loader = LOADERS[lang];
  if (!loader) return Promise.resolve(false);
  // Not an `async` function, so that two callers racing get the SAME promise
  // rather than two wrappers around it — which is what makes "loaded once"
  // something a test can assert rather than assume.
  if (!pending.has(lang)) {
    pending.set(
      lang,
      loader()
        .then((module) => {
          LOCALES[lang] = module.default ?? module;
          return true;
        })
        .catch(() => {
          // A phone that lost signal between the shell and this file. The
          // app stays in Lithuanian and stays usable; retrying is the next
          // language switch, or the next load.
          pending.delete(lang);
          return false;
        }),
    );
  }
  return pending.get(lang);
}

/** LT is the primary market and the base dictionary: an unknown key falls
 *  back through the active language -> LT -> the key itself, so a missing
 *  translation degrades to Lithuanian rather than to a blank cell. */
export const DEFAULT_LANG = 'lt';

/**
 * Translate `key` in `lang`, interpolating `{placeholder}` tokens.
 * The legacy demo did the same with chained `.replace('{n}', …)` calls at
 * each call site; folding it in here means new strings get interpolation
 * for free and no call site has to remember the token names twice.
 */
export function translate(lang, key, vars) {
  const dict = LOCALES[lang] || LOCALES[DEFAULT_LANG];
  const raw = dict[key] ?? LOCALES[DEFAULT_LANG][key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
}
